import React, {useEffect, useRef} from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import {prefersReducedMotion} from "../../utils";

// The bundler renames Leaflet's marker images, so point the default icon at the imported files.
L.Icon.Default.mergeOptions({iconUrl: markerIcon, iconRetinaUrl: markerIcon2x, shadowUrl: markerShadow});

const escapeHtml = (value) => String(value).replace(/[&<>"']/g, c => (
    {'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]
));

const LocationMap = ({spots, country}) => {
    const elRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef(null);
    const fittedRef = useRef(undefined);

    useEffect(() => {
        const map = L.map(elRef.current, {scrollWheelZoom: false, zoomControl: true});
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19, attribution: '&copy; OpenStreetMap contributors'
        }).addTo(map);
        mapRef.current = map;
        markersRef.current = L.layerGroup().addTo(map);
        return () => {
            map.remove();
            mapRef.current = null;
            fittedRef.current = undefined;
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        markersRef.current.clearLayers();
        spots.forEach(s => {
            // Boxes that cannot be booked right now get a faded pin.
            const open = !s.booking || s.booking.bookable;
            L.marker([s.lat, s.lng], {opacity: open ? 1 : 0.55})
                .bindPopup('<strong>' + escapeHtml(s.name) + '</strong><br>' + escapeHtml(s.address) +
                    (s.booking ? '<br><em>' + escapeHtml(s.booking.label) + '</em>' : ''))
                .addTo(markersRef.current);
        });
        // Re-frame only when the country filter changed, so the map does not snap back while panning.
        if (spots.length && fittedRef.current !== country) {
            const firstFit = fittedRef.current === undefined;
            fittedRef.current = country;
            const bounds = L.latLngBounds(spots.map(s => [s.lat, s.lng]));
            // A single-box country gives zero-area bounds, so maxZoom decides the zoom there.
            const opts = {padding: [36, 36], maxZoom: country === 'All' ? 8 : 11};
            if (!firstFit && !prefersReducedMotion()) {
                map.flyToBounds(bounds, {duration: 0.9, ...opts});
            } else {
                map.fitBounds(bounds, opts);
            }
        }
        const timer = setTimeout(() => map.invalidateSize(), 0);
        return () => clearTimeout(timer);
    }, [spots, country]);

    return (
        // isolation:isolate keeps Leaflet's high z-indexes below the fixed header and the dialog.
        <div className="pb-map-wrap">
            <div ref={elRef} role="application" aria-label="Paddlers Box locations map" className="pb-map"/>
        </div>
    );
};

export default LocationMap;
